import { useToast } from '@barfacil/barfacil-ui'
import { InfiniteData, useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useState } from 'react'

import {
  CreatePriceListBody,
  PriceListParams,
  PriceListResponse,
} from '@/api/_schemas/management'
import { createPriceList } from '@/api/management'
import { usePriceListContext } from '@/app/panel/management/price-list/_contexts/price-list-context'
import { FormCreatePriceList } from '@/app/panel/management/price-list/_schemas/create-price-list'
import { PriceListKeyItem } from '@/app/panel/management/price-list/_schemas/update-product-types'
import { ApiError } from '@/config/api'
import { keyStore } from '@/config/key-store'
import { queryClient } from '@/config/query-client'
import { FormError, handleErrors } from '@/services/fetch'

export const useCreatePriceList = (keyItem: PriceListKeyItem = 'infinite') => {
  const [formErrors, setFormErrors] = useState<FormError[]>([])

  const { toastDestructive, toastPositive } = useToast()
  const { filters, configureFilters, setCurrentItem } = usePriceListContext()

  const filterObject = {
    infinite: filters,
    configureInfinite: configureFilters,
  }

  const mutation = useMutation({
    mutationFn: async (data: CreatePriceListBody) =>
      await createPriceList(data),
    onSuccess: (data) => {
      queryClient.setQueryData<InfiniteData<PriceListResponse>>(
        [...keyStore.priceList[keyItem].queryKey, filterObject[keyItem]],
        (oldData) =>
          oldData && {
            ...oldData,
            pages: [
              {
                data: [data, ...oldData.pages[0].data],
                meta: {
                  ...oldData.pages[0].meta,
                  total: oldData.pages[0].meta.total + 1,
                },
              },
              ...oldData.pages.slice(1),
            ],
          },
      )

      queryClient.refetchQueries({
        predicate: ({ queryKey }) => {
          const isPriceListQuery = keyStore.priceList[keyItem].queryKey.every(
            (key, index) => key === queryKey[index],
          )
          if (!isPriceListQuery) return false

          const [, queryFilters] = queryKey as [string, PriceListParams]
          return queryFilters.search !== filterObject[keyItem].search
        },
      })

      const oppositeKeyItem: PriceListKeyItem =
        keyItem === 'infinite' ? 'configureInfinite' : 'infinite'
      queryClient.refetchQueries({
        queryKey: [...keyStore.priceList[oppositeKeyItem].queryKey],
        exact: false,
      })

      if (keyItem === 'infinite') {
        setCurrentItem(data.cod_lista_preco.toString())
      }

      setFormErrors([])
      toastPositive('Lista de preço criada com sucesso!')
    },
    onError: ({ response }: AxiosError<ApiError>, data) => {
      setFormErrors(
        handleErrors<FormCreatePriceList>(
          response?.data,
          Object.keys(data) as (keyof FormCreatePriceList)[],
          toastDestructive,
        ),
      )
    },
  })

  return { mutation, formErrors }
}
